import ScrollReveal from './animations/ScrollReveal';
import Accordion from './components/Accordion';
import Header from './components/Header';
import GuideBuyButton from './components/GuideBuyButton';
import MerchShopify from './components/MerchShopify';
import TablePage from './components/TablePage';
import TableTabs from './components/TableTabs';
import ResponsiveSwiper from './components/ResponsiveSwiper';
import SmoothScroll from './singleton/SmoothScroll';
import WindowSize from './singleton/WindowSize';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import Marquee from './animations/Marquee';
import LazyVideos from './components/LazyVideos';

gsap.registerPlugin(ScrollTrigger);

class Website {
  $body: JQuery<HTMLElement>;
  $links: JQuery<HTMLElement>;
  header: Header;
  smoothScroll: typeof SmoothScroll;
  private windowSize: WindowSize;
  private membershipPricingSwipers: ResponsiveSwiper[];
  private memberpressCheckoutObserver?: MutationObserver;
  private layoutMutationObservers: MutationObserver[];
  private layoutResizeObservers: ResizeObserver[];
  private refreshFrame = 0;

  constructor() {
    this.$body = $('body');
    this.$links = $('a');
    this.header = new Header();
    this.membershipPricingSwipers = [];
    this.layoutMutationObservers = [];
    this.layoutResizeObservers = [];

    // Activate the smooth scrolling feature.
    this.smoothScroll = SmoothScroll;
    this.windowSize = WindowSize.getInstance();

    this.initObservers();

    this.initScrollingLinks();
    this.initColorShift();
    this.initGuideSwiper();
    this.initGuideBuyButtons();
    this.initMembershipPricingSwiper();
    this.initAccordion();
    this.initTableTabs();
    this.initTablePages();
    this.initMerchShopify();
    this.initMemberpressCheckoutRefresh();
    this.initAjaxLayoutRefresh();
    this.initWindowLayoutRefresh();

    this.initMarquee();
  }

  scheduleSmoothScrollRefresh() {
    if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);

    this.refreshFrame = requestAnimationFrame(() => {
      this.refreshFrame = 0;
      this.smoothScroll.refresh();
    });
  }

  initMemberpressCheckoutRefresh() {
    const checkoutSelector = 'form.mepr-signup-form.mepr-form.alignwide';
    const checkoutForm = document.querySelector(checkoutSelector);

    if (!checkoutForm) return;

    this.memberpressCheckoutObserver = new MutationObserver(() => {
      this.scheduleSmoothScrollRefresh();
    });
    this.memberpressCheckoutObserver.observe(checkoutForm, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.scheduleSmoothScrollRefresh();
  }

  initAjaxLayoutRefresh() {
    const watchedElements = document.querySelectorAll<HTMLElement>('[data-ajax-layout-watch]');
    if (!watchedElements.length) return;

    watchedElements.forEach((element) => {
      const mutationObserver = new MutationObserver(() => {
        this.scheduleSmoothScrollRefresh();
      });

      mutationObserver.observe(element, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });

      this.layoutMutationObservers.push(mutationObserver);

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(() => {
          this.scheduleSmoothScrollRefresh();
        });

        resizeObserver.observe(element);
        this.layoutResizeObservers.push(resizeObserver);
      }
    });

    this.scheduleSmoothScrollRefresh();
  }

  initWindowLayoutRefresh() {
    window.addEventListener('load', () => {
      this.scheduleSmoothScrollRefresh();
    });

    window.addEventListener('pageshow', () => {
      this.scheduleSmoothScrollRefresh();
    });

    const adminBar = document.getElementById('wpadminbar');
    if (!adminBar) return;

    const adminBarObserver = new MutationObserver(() => {
      this.scheduleSmoothScrollRefresh();
    });

    adminBarObserver.observe(adminBar, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.layoutMutationObservers.push(adminBarObserver);
  }

  initMerchShopify() {
    new MerchShopify();
  }

  initGuideBuyButtons() {
    new GuideBuyButton();
  }

  initTablePages() {
    const $tablePages = $('[data-table-page]');
    if (!$tablePages.length) return;

    $tablePages.each((_, el) => {
      const $el = $(el);
      if (!$el.length) return;

      new TablePage($el.get(0)!);
    });
  }

  initTableTabs() {
    const $tableTabs = $('[data-table-tabs]');
    if (!$tableTabs.length) return;

    $tableTabs.each((_, el) => {
      const $el = $(el);
      if (!$el.length) return;

      new TableTabs($el.get(0)!);
    });
  }

  initAccordion() {
    const $accordions = $('.accordion');
    if (!$accordions.length) return;

    $accordions.each((_, el) => {
      const $el = $(el);
      if (!$el.length) return;

      new Accordion($el.get(0)!);
    });
  }

  initMarquee() {
    const $marquee = $('.marquee');
    if (!$marquee.length) return;

    $marquee.each((_, el) => {
      const $el = $(el);
      if (!$el.length) return;

      new Marquee($el.get(0)!);
    });
  }

  initGuideSwiper() {
    const $guideSwiper = $('.guide-swiper');
    if (!$guideSwiper.length) return;

    $guideSwiper.each((_, el) => {
      const $el = $(el);
      if (!$el.length) return;

      new Swiper($el[0], {
        slidesPerView: 'auto',
        spaceBetween: 12,
        speed: 500,
        loop: true,
        modules: [Navigation, Pagination],
        pagination: {
          el: $el.find('.swiper-pagination')[0],
          clickable: true,
        },
        navigation: {
          nextEl: $el.find('.swiper-button-next')[0],
          prevEl: $el.find('.swiper-button-prev')[0],
        },
        breakpoints: {
          1024: {
            spaceBetween: 16
          },
          1280: {
            spaceBetween: 24
          }
        }
      });
    });
  }

  initMembershipPricingSwiper() {
    const $pricingSwipers = $('.membership-pricing-swiper');
    if (!$pricingSwipers.length) return;

    $pricingSwipers.each((_, el) => {
      const $el = $(el);
      if ($el.data('responsive-swiper-init')) return;

      this.membershipPricingSwipers.push(
        new ResponsiveSwiper(el, {
          centeredSlides: true,
          slidesPerView: 'auto',
          spaceBetween: 16,
          speed: 500,
          modules: [Pagination],
          pagination: {
            el: $el.find('.swiper-pagination')[0],
            clickable: true,
          },
        }, 1024)
      );

      $el.data('responsive-swiper-init', true);
    });
  }

  initScrollingLinks() {
    this.$links.on('click', this.linkClicked.bind(this));
    this.scrollOnPageLoad();
  }

  initColorShift() {
    const shiftedClass = 'is-color-shifted';
    const $colorShiftEls = $('[data-color-shift]');
    if (!$colorShiftEls.length) return;

    $colorShiftEls.each((_, el) => {
      const $el = $(el);
      const target = $el.data('target') || 'body';
      const $target = $(target);
      const start = $el.data('start') || 'top 40%';

      ScrollTrigger.create({
        trigger: $el.get(0),
        start: start,
        onEnter: () => {
          $target.addClass(shiftedClass);
        },
        onLeaveBack: () => {
          $target.removeClass(shiftedClass);
        },
      });

      // add is init class to target
      $target.addClass('is-color-shift-init');
    });
  }

  linkClicked(e: JQuery.ClickEvent) {
    const $cur = $(e.currentTarget);
    const href = $cur.attr('href');
    if (!href) return;
    const hrefArr = href.split('#');
    const { pathname, origin, host } = window.location;

    // If the href is empty or just a hash, or if it's not a hash but the
    // path doesn't match the current page, do nothing.
    if (
      !href ||
      href === '#' ||
      (!href.startsWith('#') && !href.includes('#')) ||
      (!href.startsWith('#') &&
        hrefArr[0] !== pathname &&
        hrefArr[0] !== origin + pathname &&
        hrefArr[0] !== '//' + host + pathname)
    ) {
      return;
    }

    const $target = href.startsWith('#') ? $(href) : $(`#${hrefArr[1]}`);
    if ($target.length) {
      e.preventDefault();

      if (this.header.headerIsOpened) {
        this.header.closeNav();
      }

      this.smoothScroll.scrollToSection($target);

      // update url hash
      // window.history.pushState(null, '', href);
    }
  }

  scrollOnPageLoad() {
    const { hash } = window.location;

    if (!hash || hash === '#') return;
    const $target = $(hash);
    if (!$target.length) return;

    setTimeout(() => {
      this.smoothScroll.scrollToSection($target);
    }, 200);
  }

  initObservers() {
    new ScrollReveal(
      $(
        '[data-visibility]:not([data-is-visible]), [data-visibility-inside]>*:not([data-is-visible])',
      ),
    );
    new LazyVideos();
  }
}

$(function () {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const website = new Website();
});
